import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast, Toaster } from "sonner";
import {
  Utensils, GlassWater, Cake, Soup, Coffee, Pizza, ShoppingBag, Plus, Minus,
  Trash2, CheckCircle2, Clock, ChefHat, Send, X, MapPin, Sparkles,
  Receipt, Banknote, ScrollText, BellRing, PartyPopper,
} from "lucide-react";

const PUB = `${process.env.REACT_APP_BACKEND_URL}/api/restaurant-public`;

const ICON_MAP = {
  utensils: Utensils, "glass-water": GlassWater, cake: Cake, soup: Soup,
  coffee: Coffee, pizza: Pizza,
};

// Ammattisanasto (sama kuin RestaurantDashboard, Phase 20)
const STATUS_STEPS = [
  { key: "received",  label: "Uusi tilaus",            icon: CheckCircle2, color: "text-blue-300",    dot: "bg-blue-400" },
  { key: "preparing", label: "Valmistuksessa",         icon: ChefHat,      color: "text-amber-300",   dot: "bg-amber-400" },
  { key: "ready",     label: "Valmis tarjoiltavaksi",  icon: Sparkles,     color: "text-emerald-300", dot: "bg-emerald-400" },
  { key: "served",    label: "Tarjoiltu pöytään",      icon: Utensils,     color: "text-purple-300",  dot: "bg-purple-400" },
  { key: "paid",      label: "Maksu vastaanotettu",    icon: CheckCircle2, color: "text-green-300",   dot: "bg-green-400" },
];

const formatEur = (v) => `${Number(v || 0).toFixed(2).replace(".", ",")} €`;

// ---------- Per-pöytä sessio-id (anonyymi UUID, vain localStorageessa) ----------
function loadSession() {
  try { return JSON.parse(localStorage.getItem("rest_session") || "{}"); }
  catch { return {}; }
}
function saveSession(s) {
  try { localStorage.setItem("rest_session", JSON.stringify(s)); } catch (_e) { /* ignore */ }
}
function makeUUID() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "cust_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}
function getOrCreateSessionId(qrToken) {
  const s = loadSession();
  if (!s[qrToken]?.session_id) {
    s[qrToken] = { ...(s[qrToken] || {}), session_id: makeUUID() };
    saveSession(s);
  }
  return s[qrToken].session_id;
}
function clearQrSession(qrToken) {
  const s = loadSession();
  delete s[qrToken];
  saveSession(s);
}

export default function RestaurantPublicMenu() {
  const { qrToken } = useParams();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [table, setTable] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Phase 20 – monitilaus-sessio
  const [sessionId] = useState(() => getOrCreateSessionId(qrToken));
  const [myOrders, setMyOrders] = useState([]);
  const [view, setView] = useState("menu");           // "menu" | "orders" | "thanks"
  const [requestingBillFor, setRequestingBillFor] = useState(null);
  const prevAllPaidRef = useRef(false);

  // ---------- Load menu ----------
  const loadMenu = useCallback(async () => {
    try {
      const r = await axios.get(`${PUB}/menu/${qrToken}`);
      setTable(r.data.table);
      setCategories(r.data.categories || []);
      setProducts(r.data.products || []);
      if ((r.data.categories || []).length > 0) setActiveCat(r.data.categories[0].id);
    } catch (e) {
      setErr(e?.response?.data?.detail || "QR-koodi on virheellinen tai pöytä ei ole käytössä.");
    }
    setLoading(false);
  }, [qrToken]);

  useEffect(() => {
    loadMenu();
  }, [loadMenu]);

  // ---------- Poll /my-orders 5s välein (Phase 20) ----------
  useEffect(() => {
    let cancelled = false;
    const fetchMine = async () => {
      try {
        const r = await axios.get(`${PUB}/my-orders`, {
          params: { qr_token: qrToken, session_id: sessionId },
        });
        if (cancelled) return;
        setMyOrders(r.data.orders || []);
        // Tunnista hetki, jolloin viimeinenkin tilaus muuttuu maksetuksi
        const allPaidNow = r.data.all_paid && r.data.count > 0;
        if (allPaidNow && !prevAllPaidRef.current) {
          // Kiitos-näkymä 3 sek, sitten nollaa sessio + palaa menuun
          setView("thanks");
          setTimeout(() => {
            if (cancelled) return;
            clearQrSession(qrToken);
            setMyOrders([]);
            setView("menu");
          }, 3000);
        }
        prevAllPaidRef.current = allPaidNow;
      } catch (_e) { /* hiljaa */ }
    };
    fetchMine();
    const i = setInterval(fetchMine, 5000);
    return () => { cancelled = true; clearInterval(i); };
  }, [qrToken, sessionId]);

  const activeOrdersCount = myOrders.filter(o => !o.is_finished).length;

  // ---------- Cart ----------
  const addToCart = (product) => {
    setCart(prev => {
      const ex = prev.findIndex(i => i.product_id === product.id);
      if (ex >= 0) {
        const copy = [...prev];
        copy[ex] = { ...copy[ex], quantity: copy[ex].quantity + 1 };
        return copy;
      }
      return [...prev, {
        product_id: product.id,
        name: product.name,
        price: product.price,
        quantity: 1,
        vat_percent: product.vat_percent || 14,
      }];
    });
    toast.success(`${product.name} lisätty`, { duration: 1200 });
  };

  const updateQty = (idx, delta) => {
    setCart(prev => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], quantity: Math.max(0, copy[idx].quantity + delta) };
      return copy.filter(i => i.quantity > 0);
    });
  };

  const removeFromCart = (idx) => setCart(prev => prev.filter((_, i) => i !== idx));

  const cartTotal = useMemo(
    () => cart.reduce((sum, it) => sum + it.price * it.quantity, 0),
    [cart],
  );
  const cartCount = useMemo(
    () => cart.reduce((sum, it) => sum + it.quantity, 0),
    [cart],
  );

  // ---------- Submit ----------
  const submitOrder = async () => {
    if (cart.length === 0) return toast.error("Lisää tuotteita ostoskoriin");
    setSubmitting(true);
    try {
      const r = await axios.post(`${PUB}/order`, {
        qr_token: qrToken,
        items: cart,
        comment,
        customer_name: customerName,
        session_id: sessionId,
      });
      setCart([]);
      setComment("");
      setCartOpen(false);
      toast.success(`Tilaus ${r.data.order_number} vastaanotettu!`);
      // Siirry "Omat tilaukset" -välilehdelle näyttämään juuri lähetetty tilaus
      setView("orders");
      // Optimistic add (pollaus täydentää 5s kuluessa)
      const optimistic = {
        id: r.data.order_id,
        order_number: r.data.order_number,
        table_number: table?.table_number,
        status: r.data.status || "received",
        items: cart,
        total: r.data.total,
        comment,
        is_finished: false,
        bill_requested: false,
        created_at: new Date().toISOString(),
      };
      setMyOrders(prev => {
        if (prev.some(o => o.id === optimistic.id)) return prev;
        return [optimistic, ...prev];
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tilauksen lähetys epäonnistui");
    }
    setSubmitting(false);
  };

  const requestBill = async (order) => {
    if (order.bill_requested) {
      toast.info("Lasku on jo pyydetty – henkilökunta on matkalla.");
      return;
    }
    setRequestingBillFor(order.id);
    try {
      await axios.post(`${PUB}/order/${order.id}/request-bill`, { session_id: sessionId });
      toast.success("Lasku pyydetty – henkilökunta tulee luoksesi pian!");
      // Optimistinen päivitys
      setMyOrders(prev => prev.map(o => o.id === order.id ? { ...o, bill_requested: true, bill_requested_at: new Date().toISOString() } : o));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lipun pyyntö epäonnistui");
    }
    setRequestingBillFor(null);
  };

  // ---------- UI: error ----------
  if (err) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-black to-zinc-950 text-white flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-4" data-testid="public-error">
          <div className="text-5xl">⚠️</div>
          <h1 className="text-2xl font-display">Pöytää ei löytynyt</h1>
          <p className="text-white/60 text-sm">{err}</p>
          <p className="text-white/40 text-xs">Pyydä henkilökunnalta apua.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center">
        <div className="text-white/40 text-sm" data-testid="public-loading">Ladataan menua…</div>
      </div>
    );
  }

  // ---------- UI: Kiitos-näyttö (3 sek) ----------
  if (view === "thanks") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-black to-zinc-950 text-white flex items-center justify-center p-6" data-testid="thanks-screen">
        <Toaster richColors position="top-center" />
        <div className="max-w-md text-center space-y-5 animate-in fade-in zoom-in duration-500">
          <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-amber-400 via-orange-500 to-red-500 flex items-center justify-center shadow-2xl shadow-amber-500/40">
            <PartyPopper className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-4xl font-display gradient-text-luxe">Kiitos käynnistäsi!</h1>
          <p className="text-white/70">Maksu on vastaanotettu onnistuneesti.</p>
          <p className="text-xs text-white/40">Tervetuloa uudelleen pian 🍽️</p>
          <div className="text-[10px] text-white/30">Palaamme menuun automaattisesti…</div>
        </div>
      </div>
    );
  }

  // ---------- UI: menu ----------
  const activeProducts = activeCat
    ? products.filter(p => p.category_id === activeCat)
    : products;

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-black to-zinc-950 text-white pb-32" data-testid="public-menu">
      <Toaster richColors position="top-center" />

      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-zinc-950/80 border-b border-white/10 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-amber-300/80">Ravintolan menu</p>
            <h1 className="text-lg font-display tracking-tight flex items-center gap-2 truncate">
              Pöytä {table?.table_number}
              {table?.location && <span className="text-xs text-white/40 flex items-center gap-1"><MapPin className="w-3 h-3" />{table.location}</span>}
            </h1>
          </div>
          <Button
            onClick={() => setCartOpen(true)}
            disabled={cart.length === 0}
            className="btn-luxe relative inline-flex items-center gap-2 flex-shrink-0"
            data-testid="open-cart"
          >
            <ShoppingBag className="w-4 h-4" />
            <span className="hidden sm:inline">Ostoskori</span>
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-400 text-zinc-900 text-[10px] font-bold flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </Button>
        </div>

        {/* 3-välilehti tab-rivi (Phase 20) */}
        <div className="max-w-2xl mx-auto mt-2 grid grid-cols-2 gap-1 p-1 rounded-lg bg-white/[0.04] border border-white/5" data-testid="public-tabs">
          <button
            onClick={() => setView("menu")}
            className={`py-2 px-3 rounded-md text-xs transition flex items-center justify-center gap-1.5 ${view === "menu" ? "bg-amber-500/15 text-amber-200" : "text-white/50 hover:text-white"}`}
            data-testid="public-tab-menu"
          >
            <Utensils className="w-3.5 h-3.5" /> Menu
          </button>
          <button
            onClick={() => setView("orders")}
            className={`py-2 px-3 rounded-md text-xs transition flex items-center justify-center gap-1.5 ${view === "orders" ? "bg-amber-500/15 text-amber-200" : "text-white/50 hover:text-white"}`}
            data-testid="public-tab-orders"
          >
            <ScrollText className="w-3.5 h-3.5" /> Omat tilaukset
            {activeOrdersCount > 0 && (
              <span className="ml-1 w-4 h-4 rounded-full bg-amber-500 text-zinc-900 text-[9px] font-bold flex items-center justify-center" data-testid="orders-badge">
                {activeOrdersCount}
              </span>
            )}
          </button>
        </div>
      </header>

      {/* Omat tilaukset -näkymä (Phase 20) */}
      {view === "orders" && (
        <main className="max-w-2xl mx-auto px-4 py-4" data-testid="my-orders-view">
          {myOrders.length === 0 ? (
            <div className="text-center py-16 text-white/40">
              <ScrollText className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Ei vielä tilauksia tämän pöydän sessiossa.</p>
              <Button
                onClick={() => setView("menu")}
                className="mt-4 btn-luxe"
                data-testid="my-orders-go-menu"
              >
                <Utensils className="w-4 h-4 mr-2" /> Siirry tilaamaan
              </Button>
            </div>
          ) : (
            <div className="space-y-3" data-testid="my-orders-list">
              <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-3 flex items-start gap-2">
                <Receipt className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-white/70">
                  <strong className="text-amber-200">Vinkki:</strong> Näet vain omat tilauksesi.
                  Samassa pöydässä voitte maksaa <strong>erikseen</strong> – jokainen omalta puhelimelta.
                </p>
              </div>
              {myOrders.map(o => <CustomerOrderCard key={o.id} order={o} onRequestBill={requestBill} busy={requestingBillFor === o.id} />)}
            </div>
          )}
        </main>
      )}

      {/* Menu (kategorier + tuotteet) */}
      {view === "menu" && (
        <>
      {/* Category tabs - horizontal scroll */}
      <div className="sticky top-[124px] z-20 backdrop-blur-xl bg-zinc-950/70 border-b border-white/5 px-4 py-2 overflow-x-auto">
        <div className="max-w-2xl mx-auto flex gap-2" data-testid="category-tabs">
          {categories.map(c => {
            const Icon = ICON_MAP[c.icon] || Utensils;
            const isActive = c.id === activeCat;
            return (
              <button
                key={c.id}
                onClick={() => setActiveCat(c.id)}
                className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition ${
                  isActive
                    ? "bg-amber-500/15 border-amber-400/40 text-amber-200"
                    : "bg-white/[0.02] border-white/10 text-white/60 hover:text-white"
                }`}
                data-testid={`cat-tab-${c.id}`}
                style={isActive ? { boxShadow: `0 0 0 1px ${c.color}40` } : {}}
              >
                <Icon className="w-3.5 h-3.5" style={{ color: c.color }} />
                {c.name}
              </button>
            );
          })}
          {categories.length === 0 && (
            <p className="text-xs text-white/40 italic">Ei kategorioita</p>
          )}
        </div>
      </div>

      {/* Product grid */}
      <main className="max-w-2xl mx-auto px-4 py-4">
        {activeProducts.length === 0 ? (
          <div className="text-center py-16 text-white/40">
            <Utensils className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Ei tuotteita tässä osiossa juuri nyt.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="product-grid">
            {activeProducts.map(p => (
              <article
                key={p.id}
                className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden hover:border-amber-400/30 hover:bg-white/[0.04] transition"
                data-testid={`menu-item-${p.id}`}
              >
                {p.image_url && (
                  <div className="aspect-[16/9] bg-black/40 overflow-hidden">
                    <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                )}
                <div className="p-3">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="text-sm font-medium text-white">{p.name}</h3>
                    <span className="font-mono text-amber-300 text-sm whitespace-nowrap">{formatEur(p.price)}</span>
                  </div>
                  {p.description && (
                    <p className="text-[11px] text-white/50 line-clamp-2 mb-2">{p.description}</p>
                  )}
                  {p.allergens?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {p.allergens.map((a, i) => (
                        <span key={i} className="text-[9px] uppercase tracking-wider bg-amber-500/10 text-amber-300/80 border border-amber-500/20 px-1.5 py-0.5 rounded">
                          {a}
                        </span>
                      ))}
                    </div>
                  )}
                  <Button
                    onClick={() => addToCart(p)}
                    size="sm"
                    className="w-full btn-luxe inline-flex items-center justify-center gap-1.5"
                    data-testid={`add-to-cart-${p.id}`}
                  >
                    <Plus className="w-3.5 h-3.5" /> Lisää
                  </Button>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
      </>
      )}

      {/* Floating cart button (mobile) – vain menu-näkymässä */}
      {view === "menu" && cart.length > 0 && !cartOpen && (
        <div className="fixed bottom-4 left-4 right-4 z-40 max-w-2xl mx-auto">
          <Button
            onClick={() => setCartOpen(true)}
            className="w-full btn-luxe shadow-2xl shadow-amber-500/20 h-14 inline-flex items-center justify-between px-5 text-base"
            data-testid="floating-cart"
          >
            <span className="inline-flex items-center gap-2">
              <ShoppingBag className="w-5 h-5" />
              <span>{cartCount} {cartCount === 1 ? "tuote" : "tuotetta"}</span>
            </span>
            <span className="font-mono">{formatEur(cartTotal)}</span>
          </Button>
        </div>
      )}

      {/* Cart dialog */}
      <Dialog open={cartOpen} onOpenChange={setCartOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-hidden flex flex-col" data-testid="cart-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-amber-300" />
              Ostoskori · Pöytä {table?.table_number}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-2 py-2">
            {cart.length === 0 ? (
              <p className="text-center text-white/40 text-sm py-8">Ostoskori on tyhjä</p>
            ) : (
              cart.map((it, idx) => (
                <div key={idx} className="flex items-center gap-2 rounded-lg bg-white/[0.03] p-2.5" data-testid={`cart-item-${idx}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{it.name}</p>
                    <p className="text-[11px] text-amber-300 font-mono">{formatEur(it.price)} / kpl</p>
                  </div>
                  <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-1">
                    <Button size="sm" variant="ghost" onClick={() => updateQty(idx, -1)} className="h-7 w-7 p-0" data-testid={`qty-down-${idx}`}>
                      <Minus className="w-3 h-3" />
                    </Button>
                    <span className="text-sm w-5 text-center font-mono" data-testid={`qty-${idx}`}>{it.quantity}</span>
                    <Button size="sm" variant="ghost" onClick={() => updateQty(idx, 1)} className="h-7 w-7 p-0" data-testid={`qty-up-${idx}`}>
                      <Plus className="w-3 h-3" />
                    </Button>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => removeFromCart(idx)} className="text-red-300 h-7 w-7 p-0">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))
            )}
          </div>

          {cart.length > 0 && (
            <div className="space-y-3 border-t border-white/10 pt-3">
              <Input
                placeholder="Nimesi (vapaaehtoinen)"
                value={customerName}
                onChange={e => setCustomerName(e.target.value)}
                data-testid="customer-name"
              />
              <textarea
                placeholder="Erityistoiveet (esim. ei sipulia, allergiat…)"
                value={comment}
                onChange={e => setComment(e.target.value)}
                className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm min-h-[60px]"
                data-testid="order-comment"
              />
              <div className="flex justify-between items-baseline border-t border-white/10 pt-3">
                <span className="text-white/70 text-sm">Yhteensä</span>
                <span className="font-mono text-2xl text-emerald-300 font-semibold" data-testid="cart-total">{formatEur(cartTotal)}</span>
              </div>
              <Button
                onClick={submitOrder}
                disabled={submitting}
                className="w-full btn-luxe h-12 text-base inline-flex items-center justify-center gap-2"
                data-testid="submit-order"
              >
                <Send className="w-4 h-4" />
                {submitting ? "Lähetetään…" : "Lähetä tilaus"}
              </Button>
              <p className="text-[10px] text-white/40 text-center">
                Tilaus toimitetaan suoraan pöytään. Maksu hoidetaan tarjoilijalle.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// CustomerOrderCard – yksittäisen asiakkaan tilauksen seuranta + Pyydä lasku
// ============================================================================
function CustomerOrderCard({ order, onRequestBill, busy }) {
  const isFinished = order.is_finished;
  const isPaid = order.status === "paid";
  const isCancelled = order.status === "cancelled";
  const currentIdx = STATUS_STEPS.findIndex(s => s.key === order.status);
  const stMeta = STATUS_STEPS.find(s => s.key === order.status) || STATUS_STEPS[0];
  // "Pyydä lasku" sallitaan kun tilaus on tarjoiltu mutta ei vielä maksettu
  const canRequestBill = order.status === "served" || order.status === "ready";
  return (
    <div
      className={`rounded-2xl border bg-white/[0.02] p-4 transition ${
        isPaid ? "border-green-500/30 opacity-70" : isCancelled ? "border-zinc-500/30 opacity-50" : "border-white/10"
      }`}
      data-testid={`my-order-${order.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-amber-300/80">Tilaus</p>
          <h3 className="text-lg font-display tracking-tight text-amber-200">{order.order_number}</h3>
          <p className="text-[10px] text-white/40">
            {order.created_at?.slice(11, 16)} · Pöytä {order.table_number}
          </p>
        </div>
        <Badge className={`text-[10px] border ${stMeta.color.replace("text-","border-")}/30 bg-white/[0.04]`}>
          <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${stMeta.dot}`}></span>
          {stMeta.label}
        </Badge>
      </div>

      {/* 5-vaiheinen aikajana (kun aktiivinen) */}
      {!isFinished && currentIdx >= 0 && (
        <div className="flex items-center gap-1 mb-3" data-testid={`my-order-timeline-${order.id}`}>
          {STATUS_STEPS.slice(0, 5).map((step, idx) => {
            const done = idx <= currentIdx;
            const current = idx === currentIdx;
            return (
              <div
                key={step.key}
                className={`flex-1 h-1.5 rounded-full transition-all ${done ? step.dot : "bg-white/5"} ${current ? "ring-2 ring-white/30 ring-offset-1 ring-offset-zinc-900" : ""}`}
                title={step.label}
              ></div>
            );
          })}
        </div>
      )}

      {/* Tuotteet */}
      <ul className="space-y-0.5 text-sm border-t border-white/5 pt-2 mb-2">
        {(order.items || []).map((it, i) => (
          <li key={i} className="flex justify-between">
            <span className="text-white/80">{it.quantity}× {it.name}</span>
            <span className="text-white/40 font-mono text-xs">{formatEur(it.quantity * it.price)}</span>
          </li>
        ))}
      </ul>
      {order.comment && (
        <p className="text-[11px] text-amber-200 bg-amber-500/10 rounded p-2 mb-2 italic">💬 {order.comment}</p>
      )}
      <div className="flex items-center justify-between text-sm border-t border-white/5 pt-2 mb-3">
        <span className="text-white/40 text-xs">Yhteensä</span>
        <span className="text-emerald-300 font-mono font-bold text-lg">{formatEur(order.total)}</span>
      </div>

      {/* Toimintapainikkeet */}
      {isPaid ? (
        <div className="rounded-lg bg-green-500/10 border border-green-500/30 p-2.5 flex items-center gap-2 text-green-200 text-sm">
          <CheckCircle2 className="w-4 h-4" />
          <span>Maksu vastaanotettu{order.payment_method ? ` · ${order.payment_method}` : ""}</span>
        </div>
      ) : isCancelled ? (
        <div className="rounded-lg bg-zinc-500/10 border border-zinc-500/30 p-2.5 text-zinc-300 text-sm">
          Tilaus peruttu
        </div>
      ) : order.bill_requested ? (
        <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-2.5 flex items-center gap-2 text-amber-200 text-sm" data-testid={`my-order-bill-pending-${order.id}`}>
          <BellRing className="w-4 h-4 animate-pulse" />
          <span>Lasku pyydetty – henkilökunta tulee luoksesi pian!</span>
        </div>
      ) : (
        <Button
          onClick={() => onRequestBill(order)}
          disabled={busy || !canRequestBill}
          className={`w-full inline-flex items-center justify-center gap-2 font-semibold py-2.5 ${
            canRequestBill
              ? "bg-gradient-to-r from-amber-500 to-orange-600 text-zinc-900 hover:from-amber-400 hover:to-orange-500"
              : "bg-white/[0.04] text-white/40 cursor-not-allowed"
          }`}
          data-testid={`my-order-request-bill-${order.id}`}
        >
          <Banknote className="w-4 h-4" />
          {canRequestBill ? "Pyydä lasku" : "Tilausta valmistellaan…"}
        </Button>
      )}
    </div>
  );
}
